import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import LoadingOverlay from '../../components/common/LoadingOverlay';

function ChurchProfile({ user }) {
  const [church, setChurch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: {
      address: '',
      city: '',
      state: '',
      country: 'India',
      pincode: ''
    }
  });
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const myChurches = await apiClient.getMyChurches(user.id);
        if (myChurches.length === 0) {
          setError('You are not assigned to any church');
          return;
        }

        const currentChurch = myChurches[0];
        setChurch(currentChurch);
        // Store in localStorage for header to display
        localStorage.setItem('selectedChurch', JSON.stringify(currentChurch));
        window.dispatchEvent(new Event('churchSelected'));

        setFormData({
          name: currentChurch.name || '',
          address: currentChurch.address || {
            address: '',
            city: '',
            state: '',
            country: 'India',
            pincode: ''
          }
        });

        const adminsData = await apiClient.getChurchAdmins(currentChurch.id);
        setAdmins(adminsData);

        const usersData = await apiClient.getAllUsers();
        setAllUsers(usersData.filter(u => u.role !== 'super_admin'));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handleChange = (field, value) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const updated = await apiClient.updateChurch(
        church.id,
        formData.name,
        formData.address,
        user.id
      );
      setChurch(updated);
      // Update localStorage with updated church data
      localStorage.setItem('selectedChurch', JSON.stringify(updated));
      window.dispatchEvent(new Event('churchSelected'));
      alert('Church profile updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const result = await apiClient.uploadChurchLogo(church.id, file, user.id);
      const updatedChurch = { ...church, logoUrl: result.logoUrl };
      setChurch(updatedChurch);
      // Update localStorage with updated church data
      localStorage.setItem('selectedChurch', JSON.stringify(updatedChurch));
      window.dispatchEvent(new Event('churchSelected'));
      alert('Logo uploaded successfully');
    } catch (err) {
      alert(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !church) {
    return <Alert severity="error">{error || 'Church not found'}</Alert>;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <LoadingOverlay open={uploadingLogo} message="Uploading logo..." />
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Church Profile
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, maxWidth: { xs: '100%', md: 800 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {/* Logo Upload */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
          <Avatar
            src={church.logoUrl}
            sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 } }}
          >
            {church.name?.charAt(0)}
          </Avatar>
          <Button variant="outlined" component="label" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Upload Logo
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleLogoUpload}
            />
          </Button>
        </Box>

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Church Name"
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            <TextField
              fullWidth
              label="Address"
              value={formData.address.address}
              onChange={(e) => handleChange('address.address', e.target.value)}
            />
            <TextField
              fullWidth
              label="City"
              value={formData.address.city}
              onChange={(e) => handleChange('address.city', e.target.value)}
            />
            <TextField
              fullWidth
              label="State"
              value={formData.address.state}
              onChange={(e) => handleChange('address.state', e.target.value)}
            />
            <TextField
              fullWidth
              label="Country"
              value={formData.address.country}
              onChange={(e) => handleChange('address.country', e.target.value)}
            />
            <TextField
              fullWidth
              label="Pincode"
              value={formData.address.pincode}
              onChange={(e) => handleChange('address.pincode', e.target.value)}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Update Profile'}
            </Button>
          </Box>
        </form>

        {/* Admins List (Read-only for church admin) */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Church Admins
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id}>
                    <TableCell>{admin.name}</TableCell>
                    <TableCell>{admin.phoneNumber}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    </Box>
  );
}

export default ChurchProfile;

